import { SyntheticEvent, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import * as releaseApi from 'api/releaseApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useCustomerStore } from 'page/ReleasePage/TaskPage/hooks/useCustomersStore';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { customersSelectors } from 'store/reducers/releaseReducers/releaseTaskReducer/customersSlice';
import {
  editActions,
  editSelectors,
} from 'store/reducers/releaseReducers/releaseTaskReducer/editSlice';
import {
  registrationActions,
  registrationSelectors,
} from 'store/reducers/releaseReducers/releaseTaskReducer/registrationSlice';
import { ReleaseEnvironment } from 'types/releaseEnvironment';
import {
  Condition,
  CONDITIONS,
  mapReleaseTaskToFormFields,
  ReleaseTask,
  ReleaseTaskFormField,
} from 'types/releaseTask';
import { EditHookReturnType } from '../types';
import { useListStore } from './useListStore';

/**
 * タスク登録 store の実装をタスクから隠すための中間層
 */
export const useEditStore = (): EditHookReturnType => {
  const dispatch = useAppDispatch();
  const { fetchReleaseTasks } = useListStore();
  const toast = useToast();
  const form = useForm<ReleaseTaskFormField>({
    mode: 'all',
  });
  const { getValues, reset, handleSubmit, control } = form;
  const currentModalStep = useAppSelector(editSelectors.currentModalStep);

  const CreateReleaseEnvironmentList = (
    list: ReleaseEnvironment,
  ): { id: string; name: string } => {
    const Environment = {
      id: list.manifestVersion ? list.manifestVersion : '',
      name: list.name,
    };

    return Environment;
  };

  const releaseEnvironments = useAppSelector(
    registrationSelectors.releaseEnvironments,
  ).map((releaseEnvironment) =>
    CreateReleaseEnvironmentList(releaseEnvironment),
  );

  const createTargetGroupList = (
    item: string,
  ): { id: string; name: string } => {
    const TargetGroupList = {
      id: item,
      name: item,
    };

    return TargetGroupList;
  };

  const targetGroupList = useAppSelector(
    registrationSelectors.targetGroupList,
  ).map((item) => createTargetGroupList(item));

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    dispatch(editActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchReleaseTasks();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const selectedCustomers = useAppSelector(
    customersSelectors.selectedCustomers,
  );

  const onSubmit = handleSubmit(
    async (values: ReleaseTaskFormField) => {
      dispatch(loaderActions.setLoading(true));
      const TaskId = values.taskId ? values.taskId : 0;
      const params: ReleaseTask = {
        taskName: values.taskName,
        startsAt: new Date(values.startsAt).toISOString(),
        endsAt: new Date(values.endsAt).toISOString(),
        group: values.group,
        condition: values.condition,
        targets:
          values.condition === CONDITIONS.ALL
            ? null
            : selectedCustomers.map((selectCustemer) => selectCustemer.id),
      };
      const result = await releaseApi.editTask(TaskId, params);
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: `タスクの登録に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: 'タスクの登録に失敗しました。',
      });
    },
  );

  const onOpenModalDelete = useCallback(() => {
    dispatch(editActions.updateIsOpenModalDelete(true));
  }, [dispatch]);

  const selectedReleaseTask = useAppSelector(editSelectors.selectedReleaseTask);

  const { fetchCustomersAndSelectTargets, uncheckAllCustomers } =
    useCustomerStore();

  useEffect(() => {
    if (selectedReleaseTask) {
      void fetchCustomersAndSelectTargets();
      // 画面を開いた時の初期値セット
      dispatch(
        registrationActions.conditionUpdated({
          condition: selectedReleaseTask.condition,
        }),
      );
      dispatch(
        registrationActions.updateTargetGroup({
          targetGroup: selectedReleaseTask.group,
        }),
      );
      reset(mapReleaseTaskToFormFields(selectedReleaseTask));
    }
  }, [selectedReleaseTask, fetchCustomersAndSelectTargets, reset, dispatch]);

  const handleChangeCondition = useCallback(
    (_condition: Condition) => {
      if (_condition === CONDITIONS.ALL) {
        uncheckAllCustomers();
      }
      dispatch(
        registrationActions.conditionUpdated({
          condition: _condition,
        }),
      );
    },
    [dispatch, uncheckAllCustomers],
  );

  /**
   * ターゲットグループを選択した時の処理です。
   *
   * `useRegistrationStore`と同じ処理だが、
   * 新規・変更で処理が変更される可能性はあり得るため、統合は諦めて分割する。
   */
  const handleChangeSelectTargetGroupInput = useCallback(
    (e: SyntheticEvent<HTMLSelectElement>) => {
      uncheckAllCustomers();
      const { value } = e.currentTarget;
      form.setValue('group', value);
      dispatch(registrationActions.updateTargetGroup({ targetGroup: value }));
    },
    [dispatch, form, uncheckAllCustomers],
  );

  const shouldShowCustomerList = useAppSelector(
    registrationSelectors.shouldShowCustomerList,
  );

  return {
    mode: '更新',
    isOpenModal: !!selectedReleaseTask,
    currentModalStep,
    form,
    control,
    releaseEnvironments,
    targetGroupList,
    shouldShowCustomerList,
    selectedCustomers,
    values: getValues(),
    handleChangeCondition,
    handleChangeSelectTargetGroupInput,
    onOpenModal: () => null,
    onCloseModal,
    onCloseModalWhenCompleted,
    onConfirm,
    onGoBack,
    onSubmit,
    onOpenModalDelete,
  };
};
